import { useEffect, useRef, useState } from 'react'

import { FeatureFlag, useFeatureFlag } from '@/hooks/useFeatureFlags'

import { NetworkStatus } from '@apollo/client'

import { getCannedCameraFeedStreamState } from '@/features/canned-video'
import { useCameraFeedQuery } from '@/graphql/generated/hooks'
import { CameraFeedQuery } from '@/graphql/generated/operations'

import { VideoFeedEvent } from '../types/types'
import { CameraStreamStateIProps, IncidentPlayerError } from '../types/types'

const TIMEOUT_IN_MS = 30_000
const MINUTE_IN_MS = 60 * 1000

export const useCameraFeed = (
  cameraId?: string,
  onEvent?: (event: VideoFeedEvent) => void
): CameraStreamStateIProps => {
  const timeoutRef = useRef(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [url, setUrl] = useState(null)
  const queryTimeoutRef = useRef(null)

  const isCannedVideoEnabled = useFeatureFlag(FeatureFlag.cannedVideo)

  const processData = (data: CameraFeedQuery) => {
    onEvent?.(VideoFeedEvent.Received)
    const { url, expiryTime } = data.streamingUrlWrapper
    if (url && expiryTime) {
      setUrl(url)
      setError(null)
      timeoutRef.current = setTimeout(refetch, MINUTE_IN_MS)
    }
  }

  const handleCompleted = (data: CameraFeedQuery) => {
    // Clear the query timeout since we received a response
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current)
      queryTimeoutRef.current = null
    }

    setIsLoading(false)
    if (data.streamingUrlWrapper) {
      processData(data)
    } else {
      setUrl(null)
      if (isCannedVideoEnabled) {
        return
      } else {
        setError(IncidentPlayerError.NO_VIDEO_FEED)
      }
    }
  }

  const handleQueryTimeout = () => {
    setIsLoading(false)
    setUrl(null)
    setError(IncidentPlayerError.ERROR_FETCHING_STREAM)
    queryTimeoutRef.current = null
  }

  const handleError = () => {
    // Clear the query timeout since we received an error response
    if (queryTimeoutRef.current) {
      clearTimeout(queryTimeoutRef.current)
      queryTimeoutRef.current = null
    }
    setIsLoading(false)
    setUrl(null)
    setError(IncidentPlayerError.ERROR_FETCHING_STREAM)
  }

  const { refetch, networkStatus } = useCameraFeedQuery({
    variables: { deviceId: cameraId },
    fetchPolicy: 'no-cache',
    skip: !cameraId || isCannedVideoEnabled,
    onCompleted: handleCompleted,
    onError: handleError,
  })

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current)
      if (queryTimeoutRef.current) {
        clearTimeout(queryTimeoutRef.current)
      }
    }
  }, [])

  useEffect(() => {
    switch (networkStatus) {
      case NetworkStatus.setVariables:
        setIsLoading(true)
        onEvent?.(VideoFeedEvent.BeginRequesting)
        break
      case NetworkStatus.loading:
        if (!queryTimeoutRef.current) {
          queryTimeoutRef.current = setTimeout(
            handleQueryTimeout,
            TIMEOUT_IN_MS
          )
        }
        break
    }
  }, [networkStatus])

  return isCannedVideoEnabled
    ? getCannedCameraFeedStreamState(cameraId)
    : { url, isLoading, errorMessage: error, refetch }
}
